import { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import withPermissionProtection from 'hoc/withPermissionProtection';

import SnackbarContext from 'context/Snackbar/Context';

import Grid from '@material-ui/core/Grid';
import SearchIcon from '@material-ui/icons/Search';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import TextFieldSsn from 'components/CncUI/TextField/TextFieldSsn';
import TextFieldSin from 'components/CncUI/TextField/TextFieldSin';
import TextFieldEmail from 'components/CncUI/TextField/TextFieldEmail';
import Radio from 'components/CncUI/Radio';
import Modal from 'components/CncUI/Modal';

import useResetEmployeeRegistration from './hooks/useResetEmployeeRegistration';

import isEmail from 'utilities/validateEmail';
import isSSN from 'utilities/validateSSN';
import isSIN from 'utilities/validateSIN';
import inputValidator from './inputValidator';

const useStyles = makeStyles(() => ({
  button: {
    width: 228,
    marginBottom: 22,
  },
}));

const ResetEmployeeButton = ({ onSuccess }) => {
  const classes = useStyles();
  const notify = useContext(SnackbarContext);
  const resetEmployeeRegistration = useResetEmployeeRegistration();

  const [open, setOpen] = useState(false);
  const {
    handleSubmit,
    errors,
    reset,
    watch,
    setValue,
    control,
    formState,
  } = useForm({
    mode: 'all',
    defaultValues: { idType: 'ssn', search: '' },
  });

  const search = watch('search');
  const idType = watch('idType');
  const resetDisabled = !formState.isValid;

  const resetEmployee = () => {
    const validator = idType === 'ssn' ? isSSN : isSIN;
    const ssn = validator(search) ? search : '';
    const email = isEmail(search) ? search : '';
    if (!ssn && !email) return;

    const confirmation = window.confirm(
      `Are you sure? Resetting user "${ssn || email}"`,
    );

    if (confirmation) {
      resetEmployeeRegistration(ssn, email).then(() => {
        if (onSuccess) onSuccess();
        notify({
          type: 'success',
          show: true,
          message: 'Employee registration reset.',
        });
      });
    }
  };

  const onClose = () => {
    reset();
    setOpen(false);
  };

  const TextInput =
    idType === 'ssn'
      ? TextFieldSsn
      : idType === 'sin'
      ? TextFieldSin
      : TextFieldEmail;

  return (
    <>
      <Button
        id="ResetEmployeeButton"
        onClick={() => setOpen(true)}
        className={classes.button}
        secondary
      >
        Reset Employee Status
      </Button>

      <Modal
        id="ResetEmployeeModal"
        open={open}
        onClose={onClose}
        scroll={'paper'}
        aria-label="ResetEmployeeModal"
        classes={{ paper: classes.dialog }}
      >
        <form onSubmit={handleSubmit(resetEmployee)}>
          <Modal.Title onClose={onClose}>Reset Employee Status</Modal.Title>
          <Modal.Content>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="body2">
                  Enter the employee’s full SSN, SIN, or email address to reset
                  their employee status.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" style={{ marginBottom: 0 }}>
                  Identitification Type
                </Typography>
                <Controller
                  render={props => (
                    <Radio.Group
                      id="ResetEmployeeModal-radioGroup"
                      value={props.value}
                      onChange={e => {
                        props.onChange(e.currentTarget.value);
                      }}
                      row
                      style={{ marginBottom: 16 }}
                    >
                      <Radio value="ssn" id="ResetEmployeeModal-ssn">
                        SSN
                      </Radio>
                      <Radio value="sin" id="ResetEmployeeModal-sin">
                        SIN
                      </Radio>
                      <Radio value="email" id="ResetEmployeeModal-email">
                        Email
                      </Radio>
                    </Radio.Group>
                  )}
                  name="idType"
                  control={control}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  render={props => (
                    <TextInput
                      light
                      fullWidth
                      id="ResetEmployeeModal-searchField"
                      label="Search"
                      onClear={() => setValue('search', '')}
                      startIcon={<SearchIcon />}
                      value={props.value}
                      onChange={e => props.onChange(e.target.value)}
                      onBlur={props.onBlur}
                      error={Boolean(errors.search)}
                      helperText={
                        (errors.search && errors.search.message) || ' '
                      }
                      className={classes.field}
                      {...(idType !== 'email'
                        ? { toggle: false, showAll: true }
                        : {})}
                    />
                  )}
                  name="search"
                  control={control}
                  rules={inputValidator(idType, false)}
                />
              </Grid>
            </Grid>
          </Modal.Content>
          <Modal.Actions>
            <Button id="ResetEmployeeModal-cancel" onClick={onClose} secondary>
              Cancel
            </Button>
            <Button
              id="ResetEmployeeModal-reset"
              disabled={resetDisabled}
              type="submit"
            >
              Reset Status
            </Button>
          </Modal.Actions>
        </form>
      </Modal>
    </>
  );
};

export default withPermissionProtection({
  permissions: 'employee_registrations:edit',
  fallback: null,
})(ResetEmployeeButton);
